<template>
  <div class="phone-input-wrapper">
    <div class="phone-input-container">
      <IntlTelInput
        ref="phoneInput"
        v-model="internalValue"
        :options="mergedOptions"
        :inputProps="mergedInputProps"
        @changeNumber="handlePhoneChange"
        @changeCountry="handleCountryChange"
      />
    </div>
    <div v-if="showErrors" class="phone-error-messages mt-1">
      <small class="text-danger fw-bold text-xs d-none phone-empty-message">
        {{ emptyMessage }}
      </small>
      <small class="text-danger fw-bold text-xs d-none phone-invalid-message">
        {{ errorMessage }}
      </small>
      <small class="text-danger fw-bold text-xs d-none phone-exists-message">
        {{ existsMessage }}
      </small>
    </div>
  </div>
</template>

<script>
import IntlTelInput from "intl-tel-input/vueWithUtils";
import "intl-tel-input/styles";

// Error messages mapping for intl-tel-input error codes
const ERROR_MESSAGES = {
  1: "Invalid country code",
  2: "Phone number is too short",
  3: "Phone number is too long",
  4: "Phone number is only valid locally",
  5: "Invalid phone number length",
};

// Default max lengths per country
const COUNTRY_MAX_LENGTHS = {
  za: 15,
  ae: 14,
  us: 19,
  gb: 15,
  ca: 19,
  na: 15,
};

export default {
  name: "PhoneInput",
  components: {
    IntlTelInput,
  },
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    // Custom options to override defaults
    options: {
      type: Object,
      default: () => ({}),
    },
    // Input element props
    inputProps: {
      type: Object,
      default: () => ({}),
    },
    // Custom input ID
    inputId: {
      type: String,
      default: "phone_number",
    },
    // Input CSS class
    inputClass: {
      type: String,
      default: "form-control text-xs",
    },
    // Whether to show built-in error messages
    showErrors: {
      type: Boolean,
      default: true,
    },
    // Custom error messages
    emptyMessage: {
      type: String,
      default: "Please enter your phone number",
    },
    invalidMessage: {
      type: String,
      default: "", // Will use computed error message if not provided
    },
    existsMessage: {
      type: String,
      default: "This phone number already exists",
    },
    // Max length override
    maxLength: {
      type: [Number, String],
      default: null,
    },
    onlyCountries: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:modelValue", "change"],
  data() {
    return {
      internalValue: this.modelValue,
      isValid: false,
      errorCode: null,
      currentCountryCode: "za",
      isInitialized: false,
    };
  },
  computed: {
    allowedCountries() {
      return this.onlyCountries && this.onlyCountries.length > 0
        ? this.onlyCountries.map((c) => c.toLowerCase())
        : null;
    },
    fallbackCountry() {
      return this.allowedCountries && !this.allowedCountries.includes("za")
        ? this.allowedCountries[0]
        : "za";
    },
    mergedOptions() {
      const baseOptions = {
        autoPlaceholder: "aggressive",
        strictMode: true,
        initialCountry: this.fallbackCountry,
        validationNumberType: "MOBILE",
      };

      // Handle country restrictions
      if (this.allowedCountries) {
        baseOptions.onlyCountries = this.allowedCountries;
      }

      return { ...baseOptions, ...this.options };
    },
    mergedInputProps() {
      return {
        id: this.inputId,
        class: this.inputClass,
        autocomplete: "tel",
        maxlength: this.computedMaxLength,
        ...this.inputProps,
      };
    },
    computedMaxLength() {
      if (this.maxLength) {
        return this.maxLength;
      }
      return COUNTRY_MAX_LENGTHS[this.currentCountryCode] || 20;
    },
    errorMessage() {
      if (this.invalidMessage) {
        return this.invalidMessage;
      }
      if (this.errorCode === null || this.isValid) {
        return "";
      }
      return ERROR_MESSAGES[this.errorCode] || "Invalid phone number";
    },
  },
  mounted() {
    this.$nextTick(() => {
      setTimeout(() => {
        this.isInitialized = true;
        this.performGeoLookup();
      }, 100);
    });
  },
  watch: {
    modelValue(newVal) {
      if (newVal !== this.internalValue) {
        this.internalValue = newVal;
      }
    },
  },
  methods: {
    /**
     * Perform geo lookup and set country after component is initialized
     */
    performGeoLookup() {
      fetch("https://ipapi.co/json/")
        .then((res) => {
          if (!res.ok) throw new Error("Geo lookup failed");
          return res.json();
        })
        .then((data) => {
          let countryCode = data.country_code
            ? data.country_code.toLowerCase()
            : this.fallbackCountry;
          // If country restrictions exist and detected country is not in the list, use fallback
          if (
            this.allowedCountries &&
            !this.allowedCountries.includes(countryCode)
          ) {
            countryCode = this.fallbackCountry;
          }
          // Only set country if different from current
          if (countryCode !== this.currentCountryCode) {
            const instance = this.$refs.phoneInput?.instance;
            if (instance && typeof instance.setCountry === "function") {
              instance.setCountry(countryCode);
            }
          }
        })
        .catch(() => {
          // Geo lookup failed, keep fallback country
        });
    },
    handlePhoneChange(number) {
      this.internalValue = number;
      this.$emit("update:modelValue", number);
      this.$emit("change", number);
      this.hideErrors();

      // Update validity state after number change
      this.$nextTick(() => {
        this.updateValidityState();
      });
    },
    /**
     * Update internal validity state
     */
    updateValidityState() {
      if (!this.isInitialized) {
        return;
      }
      const instance = this.$refs.phoneInput?.instance;
      if (
        instance &&
        typeof instance.isValidNumber === "function" &&
        typeof instance.getValidationError === "function"
      ) {
        try {
          this.isValid = instance.isValidNumber();
          this.errorCode = instance.getValidationError();
        } catch (e) {
          // Utils might not be ready yet, ignore
        }
      }
    },
    handleCountryChange(countryData) {
      if (!countryData || !countryData.iso2) {
        return;
      }
      this.currentCountryCode = countryData.iso2;

      if (this.isInitialized) {
        this.$nextTick(() => {
          this.updateValidityState();
        });
      }
    },
    /**
     * Get the full international phone number
     * @returns {string} The formatted international phone number
     */
    getFullNumber() {
      if (this.$refs.phoneInput?.instance) {
        return this.$refs.phoneInput.instance.getNumber();
      }
      return this.internalValue;
    },
    /**
     * Validate the phone number
     * @returns {boolean} Whether the phone number is valid
     */
    validate() {
      const instance = this.$refs.phoneInput?.instance;
      if (
        !instance ||
        typeof instance.isValidNumber !== "function" ||
        typeof instance.getValidationError !== "function"
      ) {
        return false;
      }

      if (!this.internalValue || !this.internalValue.trim()) {
        this.showEmptyError();
        return false;
      }

      try {
        this.isValid = instance.isValidNumber();
        this.errorCode = instance.getValidationError();

        if (!this.isValid) {
          this.showInvalidError();
        }

        return this.isValid;
      } catch (e) {
        return false;
      }
    },
    /**
     * Get the intl-tel-input instance for advanced usage
     * @returns {object|null} The intl-tel-input instance
     */
    getInstance() {
      return this.$refs.phoneInput?.instance || null;
    },
    /**
     * Show the empty field error
     */
    showEmptyError() {
      if (this.showErrors) {
        this.$el
          .querySelector(".phone-empty-message")
          ?.classList.remove("d-none");
        this.autoHideError(".phone-empty-message");
      }
    },
    /**
     * Show the invalid phone error
     */
    showInvalidError() {
      if (this.showErrors) {
        this.$el
          .querySelector(".phone-invalid-message")
          ?.classList.remove("d-none");
        this.autoHideError(".phone-invalid-message");
      }
    },
    /**
     * Show the phone exists error
     */
    showExistsError() {
      if (this.showErrors) {
        this.$el
          .querySelector(".phone-exists-message")
          ?.classList.remove("d-none");
        this.autoHideError(".phone-exists-message");
      }
    },
    /**
     * Hide all error messages
     */
    hideErrors() {
      if (this.showErrors) {
        this.$el
          .querySelectorAll(".phone-error-messages small")
          .forEach((el) => {
            el.classList.add("d-none");
          });
      }
    },
    /**
     * Auto-hide error after timeout
     */
    autoHideError(selector, duration = 3000) {
      setTimeout(() => {
        this.$el.querySelector(selector)?.classList.add("d-none");
      }, duration);
    },
  },
};
</script>

<style scoped>
.phone-input-wrapper {
  width: 100%;
}

.phone-input-container {
  width: 100%;
}

.phone-input-container :deep(.iti) {
  width: 100%;
}

.phone-input-container :deep(.iti__tel-input) {
  width: 100%;
}

.phone-error-messages {
  display: flex;
  flex-direction: column;
}
</style>
